import { useMemo } from 'react';

import { css, styled } from '@mui/material';
import { Grid, Typography } from '@kc-console/common-ui';
import { formatValueOrDefault, useTranslation } from '@kc-console/core';

import type { FormStepTitleContent, FormStepTitleProps } from './types';

const FormStepTitle = (props: FormStepTitleProps) => {
  const { t } = useTranslation();
  const {
    activeStep,
    contents = {},
    stepLabel = (activeStep: number) => t('layout:form.step.title', { step: activeStep + 1 }),
  } = props;

  const { title, description } = useMemo(() => {
    const title = formatValueOrDefault({
      origin: contents[activeStep]?.title,
    }) as string;
    const description = contents[activeStep]?.description;

    return { title, description } as FormStepTitleContent;
  }, [activeStep, contents]);

  const stepLabelText = useMemo(() => {
    return typeof stepLabel === 'function' ? stepLabel(activeStep) : stepLabel;
  }, [activeStep, stepLabel]);

  return (
    <StyledContainer container alignItems="start" gap="8px">
      <StyledTitleBox>
        <Typography variant="label/semibold/xl" color="text/inverse/weakest/default">
          {stepLabelText}
        </Typography>
      </StyledTitleBox>
      <Grid sx={{ flex: 1 }}>
        <Grid container direction="column" gap="2px">
          <StyledTitleBox>
            <Typography variant="label/semibold/xl" color="text/default">
              {title}
            </Typography>
          </StyledTitleBox>
          {description && (
            <Typography variant="label/regular/sm" color="text/weaker/default">
              {description}
            </Typography>
          )}
        </Grid>
      </Grid>
    </StyledContainer>
  );
};

export default FormStepTitle;

const StyledContainer = styled(Grid)(
  () => css`
    margin: 16px 0 40px 0;
  `,
);

const StyledTitleBox = styled(Grid)(
  () => css`
    display: flex;
    align-items: center;
    height: 32px;
  `,
);
